import moment from 'moment';
import { AssetDashboardSetting } from '../../../types/app/asset.types';
import {
  ChartsDataProcessProps,
  DateTimeIntegerProcess,
  DateTimeIntegerValue,
} from '../../../types/app/dataProcess';
import { utcFormat, hoursMinutesShowFormat } from '../../../types/core/date.types';

/**
 *  Když výroba nevyrábí, tak influx přeskočí hodnotu
 *  musím kontrolovat, jestli čas odpovídá správnému indexu,
 *  pokud nesouhlasí, tak doplním čas a hodnotu 0
 */
export const checkAndFillMissingValues = (values: any, dateFrom: any, dateTo: any) => {
  // tady je granularita vždy 60min
  let granularity = 60;
  let newValues: any[] = [];
  // projít values a pokud čas nebude sedět s indexem, tak doplnit 0
  // 0: {time: "2022-11-04T00:00:00Z", value: 2988}
  // 1: {time: "2022-11-04T01:00:00Z", value: 2992}
  // 2: {time: "2022-11-04T02:00:00Z", value: 2992}
  // -----
  // 3: {time: "2022-11-04T03:00:00Z", value: 0}
  // 4: {time: "2022-11-04T04:00:00Z", value: 0}
  // -----
  // 5: {time: "2022-11-04T05:00:00Z", value: 2992}
  // kolik hodin mezi dateFrom a dateTo
  let duration = moment.duration(dateTo.diff(dateFrom));
  let hours = duration.asHours();
  let numberOfValues = hours;
  let arrIndex = 0;
  for (let index = 0; index < numberOfValues; index++) {
    let timeByIndex = dateFrom
      .clone()
      .add((index + 1) * granularity, 'minutes')
      .format(utcFormat);
    if (values[arrIndex] && values[arrIndex].time === timeByIndex) {
      // záznam je ok
      newValues[index] = values[arrIndex];
      arrIndex++;
    } else {
      // záznam chybí --> doplnit 0
      newValues[index] = {
        time: timeByIndex,
        value: 0,
      };
    }
  }
  return newValues;
};

const getValOfNext = (arr: any[], actualIndex: number, granularity: number): number => {
  let value = 0;
  for (let i = 0; i < granularity; i++) {
    if (actualIndex + i > arr.length - 1) return value;
    value += arr[actualIndex + i].value;
  }
  return value;
};

/**
 * Přepočet hodnot pro vyšší granularity než 1h (influx to vrací špatně)
 */
export const getValByGranularity = (values: any, granularityM: number, timezone: any) => {
  let granularityH = granularityM / 60;
  let newValues: any[] = [];
  // offset v min aktuální timezony pro posunutí času
  let offset = moment().tz(timezone.value).utcOffset();
  let lastUsedIndex = 0;
  values.map((val: any, index: number) => {
    // pokud (index mod granularita) je 0, hodnotu do pole
    if (index % granularityH === 0) {
      lastUsedIndex = index;
      // přepočítat hodnoty za předchozí hodiny a posunout čas
      newValues = [
        ...newValues,
        {
          ...val,
          time: moment(val.time)
            .utc()
            // labely posouvám o hodinu, tak musím i hodnoty
            .add(granularityM - offset + 60, 'minutes')
            .format(utcFormat),
          value: getValOfNext(values, index, granularityH),
        },
      ];
    }
    // jsem na konci - dopočítat zbylé hodnoty
    if (index === values.length - 1) {
      newValues = [
        ...newValues,
        { ...val, value: getValOfNext(values, lastUsedIndex, granularityH) },
      ];
    }
  });
  return newValues;
};

/**
 * Příprava dat na export
 * @param rows
 * @param headers
 * @returns
 */
export const prepareDataForExport = (rows: any, headers: any) => {
  let exportData: any = [];
  let exportHeaders: any[] = headers.map((header: any) => header.label);
  let sumColData: any[] = [];
  rows.map((row: any, rowIndex: number) => {
    let arr: any[] = [];
    exportHeaders.map((header: string, index: number) => {
      arr = [...arr, [header, row.data[index] === undefined ? 0 : row.data[index]]];
      // poslední řádek jsou sumy
      if (rowIndex === rows.length - 1) return;
      sumColData[index] = isNaN(sumColData[index]) ? 0 : sumColData[index];
      row.data[index] = isNaN(row.data[index]) ? 0 : row.data[index];
      sumColData[index] = sumColData[index] + row.data[index];
    });
    // přidat název counteru na začátek
    arr = [['', row.label], ...arr];
    // přidat sumy na konec
    arr = [...arr, ['∑', row.sum]];
    const exportArr = Object.fromEntries(arr);
    exportData = [...exportData, exportArr];
  });
  const sumRowKeys = Object.keys(exportData[exportData.length - 1]);
  sumRowKeys.map((key: string, index: number) => {
    if (index === 0) return;
    exportData[exportData.length - 1][key] = sumColData[index - 1];
  });
  // suma sum řádků
  exportData[exportData.length - 1]['∑'] = rows[rows.length - 1].sum;
  return exportData;
};
export const testDataProcess = (props: ChartsDataProcessProps) => {
  const { data, configs, intervals, granularity, timezone } = props;

  /**
   * Returns value from data array by datetime. If not found, 0 is returned.
   * @param time
   * @returns
   */
  const findValueInDataByTime = (
    time: string,
    iFrom: number,
    iTo: number,
    valueKey: string,
    // @ts-ignore
  ): number => data.slice(iFrom, iTo + 1).find((x) => x.time === time)?.[valueKey] || 0;

  const findAllValuesByConfig = (value: string) => {
    return configs.map((config) => {
      return {
        id: config.dataSetId,
        value: findValueInDataByTime(value, config.iFrom, config.iTo, config.dataSetValueKey),
      };
    });
  };
  /**
   * Calculate and save data for chart based on settings in props of this component.
   * Calculated data are used for visualisation and for unique identification of each value.
   */
  let processedData: DateTimeIntegerProcess[] = [];
  intervals.forEach((interval) => {
    // Start of interval
    let currDate = interval.start.clone();
    // Result datastructure. First value is start date.
    const startTime = currDate.format(utcFormat);
    processedData = [
      ...processedData,
      {
        time: startTime,
        timeObject: moment(startTime).utc(),
        values: findAllValuesByConfig(startTime),
      },
    ];
    // From start to end, step is granularity in minutes
    while (currDate.add(granularity, 'minutes').diff(interval.end) <= 0) {
      const time = currDate.format(utcFormat);
      // We need to find value in data for current datetime
      processedData = [
        ...processedData,
        {
          time,
          timeObject: moment(time).utc(),
          values: findAllValuesByConfig(time),
        },
      ];
    }
  });

  /**
   * Process data for 1 hour granularity for calculating plan when different granularity is selected
   * plan is set always for 1 hour
   */
  let processedData1h: DateTimeIntegerProcess[] = [];
  if (granularity !== 60) {
    intervals.forEach((interval) => {
      // start interval is shifted by different granularity - shift it by 1 hour
      let currDate = interval.start.add(-granularity, 'minutes').add(60, 'minutes');
      const startTime = currDate.format(utcFormat);
      processedData1h = [
        ...processedData1h,
        {
          time: startTime,
          timeObject: moment(startTime).utc(),
          values: findAllValuesByConfig(startTime),
        },
      ];
      // From start to end, step is granularity in minutes
      while (currDate.add(60, 'minutes').diff(interval.end) <= 0) {
        const time = currDate.format(utcFormat);
        // We need to find value in data for current datetime
        processedData1h = [
          ...processedData1h,
          {
            time,
            timeObject: moment(time).utc(),
            values: findAllValuesByConfig(time),
          },
        ];
      }
    });
  }

  /**
   * Generates chart labels based on processed data
   */
  let chartLabels: string[] = [];
  processedData.forEach((item, i) => {
    const startStr = item.timeObject
      ?.clone()
      .add(-1, 'hours')
      .tz(timezone)
      .format(hoursMinutesShowFormat);
    const endStr = item.timeObject?.tz(timezone).format(hoursMinutesShowFormat);
    chartLabels = [...chartLabels, `${startStr} - ${endStr}`];
  });
  if (granularity !== 60) getPlanForCurrentGranularity(processedData, processedData1h, granularity);
  return processedData;
};

/**
 * Calculates plan data for different granularity than 1h
 * @param processedData - processed data for current granularity
 * @param processedData1h - processed data for 1 hour granularity
 * @param granularity - granularity in minutes
 */
export const getPlanForCurrentGranularity = (
  processedData: DateTimeIntegerProcess[],
  processedData1h: DateTimeIntegerProcess[],
  granularity: number,
) => {
  processedData.map((item: DateTimeIntegerProcess, itemIndex: number) => {
    item.values.map((val: DateTimeIntegerValue, valueIndex: number) => {
      if (val.id.includes('plan')) {
        // ignore 0 values
        if (val.value === 0) return;
        // for lower granularity than 1h
        if (granularity < 60) {
          let koef = granularity / 60;
          let newVal = processedData[itemIndex].values[valueIndex].value * koef;
          // není 0, takže spočítat kolik záznamů to ovlivní, plán je vždy po hodině (60min)
          for (let index = 0; index <= 60 / granularity - 1; index++) {
            processedData[itemIndex - index].values[valueIndex].value = newVal;
          }
        } else {
          // for higher granularity than 1h
          let newVal = 0;
          // index with same time from processedData1h arr
          let index1h = processedData1h.findIndex(
            (x: DateTimeIntegerProcess) => x.time === processedData[itemIndex].time,
          );
          for (let index = 0; index <= granularity / 60 - 1; index++) {
            newVal += processedData1h[index1h - index].values[valueIndex].value;
          }
          // grouped value for plan by granularity
          processedData[itemIndex].values[valueIndex].value = newVal;
        }
      }
    });
  });
  return processedData;
};

/**
 * Checks if there is extra line in main dashboard
 * if returns true we have to shrink chart so there is no scrollbar
 * @param dashboardConfig
 * @returns boolean
 */
export const isExtraStatsLine = (dashboardConfig: AssetDashboardSetting) => {
  return (
    (dashboardConfig?.showHydraOrderNumber && dashboardConfig?.showHydraToolNumber) ||
    (dashboardConfig.showHydraMachineNumber && dashboardConfig.showHydraStatus)
  );
};
